import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Outlet } from 'react-router-dom'

import {
  AlarmClock,
  Bell,
  Building2,
  Calendar,
  CreditCard,
  Languages,
  Mail,
  Puzzle,
  Settings,
  ShieldCheck,
  UserCog,
} from 'lucide-react'

import { useAppSettings } from '@/stores/useAppSettings'

import { paths } from '@/config/paths'

import DashboardContent from '@/layouts/dashboard/DashboardContent'

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import toast from '@/components/toast'
import { SmartButton } from '@/components/ui-custom/SmartButton'

import SidebarNav from '../../preferences/components/sidebar-nav'

export default function AppSettingsLayout() {
  const { t } = useTranslation(['dashboard', 'common'])
  const { resetSettings } = useAppSettings()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleDeleteConfirmed = async () => {
    setIsLoading(true)
    try {
      await resetSettings()
      toast.success(t('dashboard:settings.toast.resetSuccess', 'Settings have been reset successfully'))
      setIsDeleteDialogOpen(false)
    } catch (error) {
      toast.error(t('dashboard:settings.toast.resetError', 'Error while resetting settings'))
    } finally {
      setIsLoading(false)
    }
  }

  const sidebarNavItems = [
    {
      href: paths.dashboard.settings.general.getHref(),
      title: t('dashboard:settings.nav.general', 'General'),
      icon: <Settings className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.branding.getHref(),
      title: t('dashboard:settings.nav.branding', 'Branding'),
      icon: <Settings className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.security.getHref(),
      title: t('dashboard:settings.nav.security', 'Security'),
      icon: <ShieldCheck className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.payments.getHref(),
      title: t('dashboard:settings.nav.payments', 'Payments'),
      icon: <CreditCard className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.membership.getHref(),
      title: t('dashboard:settings.nav.membership', 'Membership'),
      icon: <UserCog className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.gymOperations.getHref(),
      title: t('dashboard:settings.nav.gymOperations', 'Gym Operations'),
      icon: <Building2 className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.bookings.getHref(),
      title: t('dashboard:settings.nav.bookings', 'Bookings'),
      icon: <Calendar className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.bookingReminders.getHref(),
      title: t('dashboard:settings.nav.bookingReminders', 'Booking Reminders'),
      icon: <AlarmClock className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.localization.getHref(),
      title: t('dashboard:settings.nav.localization', 'Localization'),
      icon: <Languages className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.integrations.getHref(),
      title: t('dashboard:settings.nav.integrations', 'Integrations'),
      icon: <Puzzle className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.notifications.getHref(),
      title: t('dashboard:settings.nav.notifications', 'Notifications'),
      icon: <Bell className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.mail.getHref(),
      title: t('dashboard:settings.nav.mail', 'Mail'),
      icon: <Mail className='h-5 w-5' />,
    },
    {
      href: paths.dashboard.settings.mailBranding.getHref(),
      title: t('dashboard:settings.nav.mail_branding', 'Mail Branding'),
      icon: <Mail className='h-5 w-5' />,
    },
  ]

  return (
    <DashboardContent
      title={t('dashboard:settings.page.index.title', 'App Settings')}
      desc={t('dashboard:settings.page.index.subtitle', 'Manage your app settings')}
      headerActions={
        <SmartButton variant='secondary' onClick={() => setIsDeleteDialogOpen(true)}>
          {t('dashboard:settings.actions.reset', 'Reset Settings')}
        </SmartButton>
      }
    >
      <div className='flex h-full min-h-screen w-full max-w-4xl flex-col gap-6 lg:flex-row'>
        {/* Sidebar */}
        <aside className='lg:top-0 lg:w-4/12 lg:shrink-0'>
          <SidebarNav items={sidebarNavItems} />
        </aside>

        {/* Main content area */}
        <main className='flex-1 overflow-auto'>
          <div className='mx-auto w-full max-w-4xl'>
            <Outlet />
          </div>
        </main>
      </div>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className='rounded-lg sm:max-w-[400px]'>
          <DialogHeader>
            <DialogTitle>{t('dashboard:settings.confirmResetTitle', 'Confirm Reset')}</DialogTitle>
            <DialogDescription>
              {t(
                'dashboard:settings.confirmResetDescription',
                'Are you sure you want to reset settings? This action cannot be undone.'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className='mt-4 flex justify-end gap-2'>
            <SmartButton
              variant='outline'
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isLoading}
              className='hover:bg-gray-100 dark:hover:bg-gray-800'
            >
              {t('common:actions.cancel', 'Cancel')}
            </SmartButton>
            <SmartButton variant='destructive' onClick={handleDeleteConfirmed} disabled={isLoading}>
              {t('dashboard:settings.resetButton', 'Reset Settings')}
            </SmartButton>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardContent>
  )
}
